import React from 'react';
import {
  ListChecks, Users, GaugeCircle, BarChart3, BookOpenCheck, FileSpreadsheet,
  Building2, Rocket, SlidersHorizontal, Database, FlaskConical, Flag, CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import { Container, SectionHeading, Btn, Card } from '../components/ui';
import Reveal from '../components/Reveal';
import FAQAccordion from '../components/FAQAccordion';
import CTASection from '../components/CTASection';
import { FeatureCard } from '../components/cards';
import OnboardingHeroDemo from '../components/OnboardingHeroDemo';
import Seo from '../components/Seo';
import { faqJsonLd } from '../data/seo';

const FEATURES = [
  { icon: 'ListChecks', title: 'A plan, pre-built for you', text: 'Every organisation starts with a complete, BillPortal-authored implementation checklist — from foundation setup through testing, parallel run and cutover. No blank canvas.' },
  { icon: 'Users', title: 'Own it as a team', text: 'Assign each task to yourself or any active member of your organisation, set priority, and track who is doing what across the whole cutover.' },
  { icon: 'GaugeCircle', title: 'Portfolio progress, tracked', text: 'Volume-based tasks show real progress like 30/30 properties or 230/240 meters, read from your live BillPortal data where available.' },
  { icon: 'BookOpenCheck', title: 'Self-explanatory tasks', text: 'Each task drawer explains the prerequisites, exactly where to navigate, step-by-step setup instructions and a Go-to-Setup deep link.' },
  { icon: 'BarChart3', title: 'One glance for management', text: 'Overall completion, status counts, blockers, overdue work and the target go-live date — always current, always shareable.' },
  { icon: 'FileSpreadsheet', title: 'Export & present', text: 'Export the plan to Excel for the working team, or print a clean, management-ready PDF implementation & cutover plan.' },
];

const PHASES = [
  { icon: Building2, name: 'Foundation Setup', text: 'Users, Properties, Units, Accounts, Meters, GL codes, payment & AP file customisation, ACH/NACHA.' },
  { icon: Rocket, name: 'Automation', text: 'Bill streams (email, API, portal credentials), payment-file schedule and ENERGY STAR sandbox & production.' },
  { icon: SlidersHorizontal, name: 'Operational Control', text: 'Missing-bills monitoring and the operational KPIs your team will manage BillPortal by.' },
  { icon: Database, name: 'Historical Data', text: 'Upload historical bills and link them to the right property, account and meter context.' },
  { icon: FlaskConical, name: 'Testing', text: 'Bill-stream, payment-file (4 runs) and AP-file (4 runs) testing, plus teams & processes.' },
  { icon: ListChecks, name: 'Pre-Cutover Plan', text: '2–4 week parallel run, payment-file and AP-file readiness, and team change-management alignment.' },
  { icon: Flag, name: 'Cutover', text: 'Go live with confidence and capture your actual go-live date as the historical record.' },
];

const HOW = [
  'Understand the task', 'Assign an owner', 'Schedule the dates',
  'Perform the setup', 'Update progress', 'Report to management', 'Cut over',
];

const FAQ = [
  { q: 'What is the Onboarding Planner?', a: 'A pre-created BillPortal implementation and cutover checklist for your organisation — you assign the work, track progress and complete it.' },
  { q: 'How do I start the onboarding project?', a: 'Use Start Project, confirm the plan start date and target go-live date, then assign priorities, owners and dates to each task.' },
  { q: 'Can I delete BillPortal default tasks?', a: 'No. Standard tasks are retained so the implementation plan stays consistent. Lower the priority, or mark an optional task Not Applicable when appropriate.' },
  { q: 'Can I add our own tasks?', a: 'Yes. Add organisation-specific custom tasks to any phase and assign them like any other task.' },
  { q: 'How do I assign work?', a: 'Choose yourself or another active member of the current organisation as the task assignee. Unassigned tasks stay visible.' },
  { q: 'What do the start and end dates mean?', a: 'They are the planned start and planned target completion dates for each task. Actual completion is captured automatically when the task is completed.' },
  { q: 'What does 14/15 mean?', a: 'For volume-based tasks, the first number is the current onboarded/configured count and the second is your organisation target — for example Vendor Accounts 14/15.' },
  { q: 'Where do the setup instructions come from?', a: 'Task guidance is populated from the actual current BillPortal implementation — the correct navigation, prerequisites and setup steps for each standard task, not assumptions.' },
  { q: 'How should we use priorities?', a: 'Use Critical/High for work that can affect testing or cutover; use Medium/Low for lower-urgency work. Priority does not change the progress formula.' },
  { q: 'What should I do if a task is blocked?', a: 'Set it to Blocked, record a short reason and add updates as the blocker is worked through. Blocked tasks stay visible in the dashboard and exports.' },
  { q: 'How do I present progress to management?', a: 'Use the dashboard summary for meetings, Export to Excel for a working project list, or Print/Save as PDF for a management-ready implementation and cutover plan.' },
  { q: 'What happens after onboarding is complete?', a: 'The plan remains accessible as your implementation record, but the Onboarding Planner moves to the bottom of the UBM Onboarding submenu. You can reopen it if post-go-live work needs to resume.' },
];

export default function OnboardingPlanner() {
  return (
    <div data-testid="onboarding-planner-page">
      <Seo
        title="Onboarding Planner — Go Live with Confidence"
        description="BillPortal's Onboarding Planner gives every organisation a pre-built implementation and cutover checklist. Plan, assign and track every onboarding activity from setup to go-live in one collaborative workspace."
        keywords="utility onboarding, implementation planner, cutover tracker, BillPortal onboarding, go-live checklist, UBM onboarding"
        jsonLd={faqJsonLd(FAQ)}
      />

      {/* HERO — max ~850px */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white dark:border-white/10 dark:bg-[#0a0a0b]">
        {/* subtle blue radial gradients */}
        <div className="pointer-events-none absolute inset-0" aria-hidden style={{ background: 'radial-gradient(60rem 40rem at 85% -10%, rgba(37,99,235,0.10), transparent 60%), radial-gradient(40rem 30rem at 0% 110%, rgba(37,99,235,0.07), transparent 55%)' }} />
        <Container className="relative">
          <div className="grid min-h-[520px] grid-cols-1 items-center gap-10 py-12 lg:min-h-[720px] lg:grid-cols-2 lg:gap-14 lg:py-16">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Enterprise onboarding, orchestrated
              </span>
              <h1 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-slate-900 dark:text-white sm:text-5xl lg:text-6xl">
                Go live with confidence.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
                Plan, assign and track every onboarding activity — from initial setup to successful cutover — in one
                collaborative workspace.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Btn to="/demo" size="lg" arrow data-testid="onboarding-hero-start">Start your onboarding</Btn>
                <Btn href="#how-it-works" size="lg" variant="outline" data-testid="onboarding-hero-how">See how it works</Btn>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand" /> 30 guided tasks</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand" /> Team ownership</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand" /> Cutover-ready</span>
              </div>
            </div>
            <div className="w-full">
              <OnboardingHeroDemo />
            </div>
          </div>
        </Container>
      </section>

      {/* WHY A PLANNER, NOT A WIZARD */}
      <section className="border-b border-slate-200 bg-white px-5 py-16 dark:border-white/10 dark:bg-[#0a0a0b] sm:px-8 sm:py-20" data-testid="onboarding-why-planner">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <Reveal>
              <SectionHeading align="left" eyebrow="Why a planner" title="Clear direction to get started — and to go live" />
            </Reveal>
            <Reveal delay={80}>
              <div className="space-y-4 text-base leading-relaxed text-slate-600 dark:text-slate-300">
                <p>UBM onboarding is genuinely complex — many moving parts and dependencies that don't fit neatly into a rigid, wizard-driven flow. So instead of a wizard, we give you a <strong className="text-slate-900 dark:text-white">ready-made plan</strong>.</p>
                <p>The Onboarding Planner shows your organisation exactly what's needed to get set up <strong className="text-slate-900 dark:text-white">straight away</strong> — an out-of-the-box, handy planning tool you can follow all the way from initial setup to go-live.</p>
                <p>And because the plan is already structured, you can <strong className="text-slate-900 dark:text-white">present it to management in minutes</strong> — with owners, dates and progress everyone can see. That's the crux of it.</p>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* FEATURES */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Why teams love it" title="Your implementation, structured from day one" subtitle="BillPortal gives you the plan. Your team owns the work. Every standard task is self-explanatory." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (<Reveal key={f.title} delay={i * 60}><FeatureCard icon={f.icon} title={f.title} text={f.text} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* PHASES */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="The journey" title="Seven phases, one clear path to go-live" subtitle="A predefined catalogue of tasks, grouped the way real utility onboarding actually happens." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PHASES.map((p, i) => (
              <Reveal key={p.name} delay={i * 50}>
                <Card className="flex h-full items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand"><p.icon className="h-5 w-5" /></span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] font-bold text-slate-300 dark:text-slate-600">{String(i + 1).padStart(2, '0')}</span>
                      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{p.name}</h3>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{p.text}</p>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="scroll-mt-20 px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="How it works" title="Understand → assign → set up → complete → cut over" subtitle="A guided implementation planner and progress tracker — not another project-management tool to learn." /></Reveal>
          <div className="mt-12 flex flex-wrap items-stretch justify-center gap-3">
            {HOW.map((step, i) => (
              <Reveal key={step} delay={i * 50} className="flex items-center gap-3">
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">{i + 1}</span>
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{step}</span>
                </div>
                {i < HOW.length - 1 && <ArrowRight className="hidden h-4 w-4 text-slate-300 lg:block" />}
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mx-auto mt-10 flex max-w-2xl items-center justify-center gap-2 rounded-2xl border border-brand/20 bg-brand/[0.04] p-4 text-center">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" />
              <p className="text-sm text-slate-600 dark:text-slate-300">BillPortal provides the plan upfront. Your team assigns the work, tracks progress and completes it — with the exact setup guidance for every task.</p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-20">
        <Container>
          <Reveal><SectionHeading eyebrow="FAQ" title="Onboarding Planner — questions & answers" /></Reveal>
          <Reveal delay={80} className="mt-10"><FAQAccordion items={FAQ} /></Reveal>
        </Container>
      </section>

      <CTASection />
    </div>
  );
}
